import {
  App,
  Col,
  Space,
  Form,
  Row,
  Table,
  Input,
  Button,
  Tooltip
} from 'antd';
import { t } from 'i18next';
import { ChangeEventHandler, FC, useCallback, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { FULL_DATE_FORMAT } from 'constants/dates';
import { USER_ROLES } from 'constants/users';
import { EllipsisOutlined, PlusOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { PERMISSION_ROUTES } from 'constants/permissions';
import { deleteUser, fetchUsers } from 'services/api/users';
import { UserListRow } from 'types/api/users';
import { useUIDispatchContext } from 'providers/UI';

import useOrgEffect from 'hooks/useOrgEffect';
import usePermission from 'hooks/usePermission';
import { SUPER_ADMIN_ROLE } from 'hooks/useIsSuperAdminRole';
import useRolesById from 'hooks/useRolesById';
import { showErrorMessage } from 'utils/showErrorMessage';
import { adminActionRefusal, roleEditRefusal } from 'utils/ceiling';
import { ActionMenu, PageWrapper } from 'components/ui';
import {
  AssignRoleModal,
  ChangeUserPassword,
  EditAdminModal,
  UsersModal
} from '../components';

const Admins: FC = () => {
  const { toggleModal } = useUIDispatchContext();
  const { message } = App.useApp();
  const [isDetailsVisible, setDetailsVisible] = useState(false);
  const [detailsId, setDetailsId] = useState('');
  const [page, setPage] = useState(1);
  const [changePasswordId, setChangePasswordId] = useState('');
  const [assignRoleFor, setAssignRoleFor] = useState<UserListRow | null>(null);
  const [editFor, setEditFor] = useState<UserListRow | null>(null);
  const [, setSearch] = useState('');
  const timer = useRef<NodeJS.Timeout | null>(null);

  // The roles list as a lookup: the Role column needs each role's label and
  // whether this operator may open its page. Whether an ADMIN may be acted
  // on used to be worked out here too, from every role's permission map; the
  // user row answers that itself now.
  const rolesById = useRolesById();
  const { checkUserPermission } = usePermission();

  /**
   * May the operator reach the role screens at all? A section-level check,
   * separate from the per-role one below: without it the link would navigate
   * to a route the guard refuses, which is a different and worse no than the
   * one we are trying to explain.
   */
  const canReachRoles = checkUserPermission(PERMISSION_ROUTES.roles);

  const [results, setResults] = useState<UserListRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  useOrgEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const res = await fetchUsers({
          page,
          limit: 10,
          role: USER_ROLES.admin
        });

        if (cancelled) return;
        setResults(res.results);
        setTotal(res.total);
      } catch (err) {
        if (!cancelled) showErrorMessage(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [page, refreshKey]);

  const refetch = useCallback(() => {
    setRefreshKey(k => k + 1);
  }, []);

  const onSearchChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    e => {
      if (timer.current) {
        clearTimeout(timer.current);
      }

      timer.current = setTimeout(() => {
        setSearch(e.target.value);
      }, 500);
    },
    []
  );

  /**
   * Delete an admin.
   *
   * CONFIRMED, AND THE CONFIRMATION IS THE ONLY GUARD. The server allows
   * this outright for a superadmin — no ceiling, no permission bit — so
   * between a mis-click and a deleted colleague there is nothing else.
   * The dialog therefore names the person and says what goes with them,
   * rather than asking whether the operator is sure.
   *
   * They are signed out everywhere the moment it lands: their sessions
   * are dropped with the account, so an admin reading the panel is
   * returned to the login screen on their next request.
   */
  const onRemove = useCallback(
    (admin: UserListRow) => {
      toggleModal('confirm', true, {
        title: 'Remove admin',
        message: `Remove ${admin.first_name} ${admin.last_name} (${admin.email})? They lose access immediately and are signed out of every device. Anything they created stays.`,
        async onConfirm() {
          try {
            await deleteUser(admin.id, 'admin');
            message.success('Admin removed');
            refetch();
          } catch (err) {
            showErrorMessage(err);
          }
        }
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [refetch]
  );

  const onDetailsClose = useCallback(() => {
    setDetailsId('');
    setDetailsVisible(false);
  }, []);

  return (
    <PageWrapper
      title="Admin users"
      color="white"
      extra={[
        <Button
          type="primary"
          key="create-role"
          onClick={() => setDetailsVisible(true)}
        >
          <PlusOutlined />
          Create user
        </Button>
      ]}
    >
      <Row>
        <Col sm={8}>
          <Space>
            <Form.Item>
              <Input.Search
                placeholder={t('common.search')}
                onChange={onSearchChange}
              />
            </Form.Item>
          </Space>
        </Col>
      </Row>
      <Table
        dataSource={results}
        columns={[
          {
            title: 'ID',
            dataIndex: 'id',
            key: 'id'
          },
          {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render(_, record) {
              return `${record.first_name} ${record.last_name}`;
            }
          },
          {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            render: value => <a href={`mailto:${value}`}>{value}</a>
          },
          {
            // `role` is the actor type and reads "admin" on every row. The
            // RBAC role is role_name — snake_case because /users goes
            // through toSnake, unlike /roles.
            //
            // role_name is the IDENTIFIER, so it reads "ceiling_test" where
            // the roles page reads "Ceiling Test (temporary)". The display
            // label lives only on /roles, so join it on role_id and fall
            // back to the identifier.
            title: 'Role',
            dataIndex: 'role_name',
            key: 'role_name',
            render: (roleName, record) => {
              const role = rolesById.get(record.role_id as number);
              const label = role?.label || role?.name || roleName || '—';

              // THE EDITOR IS THE ONLY ROLE PAGE THERE IS. There is no
              // view-only screen, so "see what this role grants" and "edit
              // this role" are the same destination, and the link may only
              // be offered to someone who may edit it. Anyone else gets the
              // label as plain text — the same as before this was a link —
              // rather than a link that bounces them back with a refusal.
              //
              // Both halves are needed: canAct is per ROLE (a system role,
              // or one granting more than the operator holds, is refused by
              // the server on read as well as write), and the permission
              // check is per SECTION. Either one failing means no link.
              const linkable =
                !!role && canReachRoles && !roleEditRefusal(role);

              if (!linkable) return label;

              return (
                <Link to={`/access/roles/details/${role.id}`}>{label}</Link>
              );
            }
          },
          {
            title: 'Created at',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (value: number) => dayjs(value).format(FULL_DATE_FORMAT)
          },
          {
            title: '',
            dataIndex: 'id',
            key: 'id',
            width: '5%',
            align: 'right',
            render: (id, record) => {
              // By ROLE, never by id. The superadmin is id 1 today and need
              // not be tomorrow — the backend parameterises this for the same
              // reason when it hides the superadmin from the admins list.
              if (record.role_name === SUPER_ADMIN_ROLE) return null;

              // Decided by the server, per row. "Below" is a lattice
              // comparison of permission maps — not a count — and the panel
              // used to run its own copy of it over a second fetch of every
              // role, because the user row carried only role_id.
              const refusal = adminActionRefusal(record);

              return (
                <ActionMenu
                  data={[
                    // Edit and Change role answer to the same flag. The
                    // server decides both from one rule — act only on an
                    // admin strictly below you — and editing someone's
                    // name and email is no smaller a thing than changing
                    // their role, so it is not gated more loosely.
                    {
                      title: refusal ? (
                        <Tooltip title={refusal}>Edit</Tooltip>
                      ) : (
                        'Edit'
                      ),
                      action: () => {
                        if (refusal) return;
                        setEditFor(record);
                      },
                      menuProps: { disabled: !!refusal }
                    },
                    {
                      title: 'Change password',
                      action: () => setChangePasswordId(id)
                    },
                    // An admin may act only on admins STRICTLY below them.
                    // Peers are refused — equal is not below — and so are
                    // incomparable pairs, so this cannot be reduced to
                    // counting permissions. A superadmin is exempt.
                    //
                    // That makes an ordinary admin equal to THEMSELVES, so
                    // this is refused on their own row: they can no longer
                    // change their own role and the action is disabled rather
                    // than offered and refused.
                    {
                      title: refusal ? (
                        <Tooltip title={refusal}>Change role</Tooltip>
                      ) : (
                        'Change role'
                      ),
                      action: () => {
                        if (refusal) return;
                        setAssignRoleFor(record);
                      },
                      menuProps: { disabled: !!refusal }
                    },
                    // HIDDEN, NOT DISABLED, when the server says no. An
                    // admin without the grant should not be shown their
                    // colleague's delete control at all — and the one case
                    // that must never appear is the operator's own row,
                    // where canAct is true and canDelete is false. A Remove
                    // wired to canAct would offer them their own deletion.
                    ...(record.canDelete
                      ? [
                          {
                            title: 'Remove',
                            action: () => onRemove(record),
                            menuProps: {
                              style: { color: 'var(--danger-color)' }
                            }
                          }
                        ]
                      : [])
                  ]}
                  placement="bottomRight"
                  icon={<EllipsisOutlined />}
                />
              );
            }
          }
        ]}
        loading={loading}
        rowKey="id"
        pagination={{
          total,
          onChange: p => setPage(p),
          showSizeChanger: false
        }}
      />
      {assignRoleFor && (
        <AssignRoleModal
          id={assignRoleFor.id}
          currentRoleId={assignRoleFor.role_id}
          onClose={() => setAssignRoleFor(null)}
          refetch={refetch}
        />
      )}
      {isDetailsVisible && (
        <UsersModal
          onClose={onDetailsClose}
          id={detailsId}
          role={USER_ROLES.admin}
          refetch={refetch}
        />
      )}
      {editFor && (
        <EditAdminModal
          admin={editFor}
          onClose={() => setEditFor(null)}
          refetch={refetch}
        />
      )}
      {!!changePasswordId && (
        <ChangeUserPassword
          id={changePasswordId}
          onClose={() => setChangePasswordId('')}
        />
      )}
    </PageWrapper>
  );
};

export default Admins;
